import { Link } from "react-router-dom";
import { ArrowRight, Users, Calendar, Wrench, ThumbsUp } from "lucide-react";

const TEXTURE_BG = "https://static.prod-images.emergentagent.com/jobs/5e0315af-7eff-4b2c-b97d-580f25e1b3ed/images/196290de01abe9ebea5fdeea0641e482fe7de4ab4d40b13ce204f63c67f076f3.png";

const TIMELINE = [
  { icon: Users, title: "Consultation", desc: "We visit your site, discuss your vision, take measurements, and understand your requirements." },
  { icon: Wrench, title: "Proposal & Selection", desc: "Receive a detailed quote with product recommendations. Visit our range to choose your perfect flooring." },
  { icon: Calendar, title: "Installation", desc: "Our expert team installs your new flooring with precision, minimal disruption, and full site cleanup." },
  { icon: ThumbsUp, title: "Quality Check", desc: "Final inspection to ensure every detail meets our exacting standards. Your satisfaction is guaranteed." },
];

export default function AboutPage() {
  return (
    <main data-testid="about-page" className="pt-20">
      {/* Header */}
      <section className="py-20 md:py-28 bg-[#0a0a0a] border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Our Story</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white mb-4">About Solid Step</h1>
          <p className="font-sans text-base text-[#A3A3A3] max-w-2xl mx-auto">
            From Floor to Finish — We've Got You Covered
          </p>
        </div>
      </section>

      {/* Story */}
      <section className="py-20 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <div>
              <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">Who We Are</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-white mb-6">
                Craftsmanship Meets Dedication
              </h2>
              <div className="space-y-4 font-sans text-[#A3A3A3] text-base leading-relaxed">
                <p>
                  Solid Step Flooring & Handyman was founded with a simple mission: to deliver exceptional flooring and handyman solutions with the care and attention to detail that every home and business deserves.
                </p>
                <p>
                  With over 15 years of experience across the flooring and handyman industries, our team of skilled professionals brings unmatched expertise to every project — from intimate residential renovations and flooring installations to outdoor maintenance, demolitions, and complete property transformations.
                </p>
                <p>
                  We pride ourselves on using premium materials sourced from trusted Australian and international suppliers, combined with techniques honed through years of hands-on experience. Whether it's new floors, a fresh coat of paint, fence repairs, or lawn care — the result always meets the Solid Step standard.
                </p>
              </div>
            </div>
            <div className="relative">
              <div className="relative overflow-hidden">
                <img
                  src="https://images.unsplash.com/photo-1773867567872-3ad1fa481082?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA7MDR8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBkYXJrJTIwaW50ZXJpb3IlMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc3NjM0MTc0OXww&ixlib=rb-4.1.0&q=85"
                  alt="Solid Step craftsmanship"
                  className="w-full h-[500px] object-cover"
                />
                <div className="absolute inset-0 border border-[#D4AF37]/20" />
              </div>
              <div className="absolute -bottom-6 -left-6 bg-[#D4AF37] p-6 z-10">
                <p className="font-serif text-3xl text-black font-bold">15+</p>
                <p className="font-sans text-xs uppercase tracking-wider text-black/70">Years Experience</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section
        className="relative py-20 md:py-32"
        data-testid="process-section"
      >
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${TEXTURE_BG})` }}
        />
        <div className="absolute inset-0 texture-overlay" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="font-sans uppercase text-xs tracking-[0.2em] text-[#D4AF37] mb-4">How We Work</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-white">Our Process</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {TIMELINE.map((step, i) => (
              <div
                key={i}
                data-testid={`process-step-${i}`}
                className="relative bg-white/5 backdrop-blur-xl border border-white/10 p-8 text-center"
              >
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-black font-serif text-sm font-bold w-8 h-8 flex items-center justify-center">
                  {i + 1}
                </div>
                <div className="w-14 h-14 mx-auto mb-5 mt-2 border border-[#D4AF37]/30 flex items-center justify-center">
                  <step.icon className="w-6 h-6 text-[#D4AF37]" />
                </div>
                <h3 className="font-serif text-lg text-white mb-3">{step.title}</h3>
                <p className="font-sans text-sm text-[#A3A3A3] leading-relaxed">{step.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[#050505]">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl text-white mb-4">
            Let's Start Your Project
          </h2>
          <p className="font-sans text-base text-[#A3A3A3] mb-8 max-w-xl mx-auto">
            Whether it's a single room or an entire property, we bring the same passion and precision to every job.
          </p>
          <Link
            to="/contact"
            data-testid="about-cta"
            className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-10 py-4 text-sm uppercase tracking-wider hover:bg-[#F0C74A] transition-colors"
          >
            Get In Touch
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </main>
  );
}
